import {
  AssistantRuntimeProvider,
  AttachmentPrimitive,
  ComposerPrimitive,
  MessagePrimitive,
  ThreadListPrimitive,
  ThreadPrimitive,
  useAuiState,
} from '@assistant-ui/react';
import { MarkdownTextPrimitive } from '@assistant-ui/react-markdown';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowUp, ChevronDown, ChevronRight, FileText, Paperclip, Plus, Square, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import remarkGfm from 'remark-gfm';
import { FeedbackBar } from '@/features/feedback/FeedbackBar';
import { useSession } from '@/lib/auth/useSession';
import { ATTACHMENT_HINT } from './attachments';
import { sessionKeys } from './queries';
import { useSimonRuntime } from './runtime';
import { useChatUrl } from './useChatUrl';
import { ArchivedCard, SessionCard } from './SessionCard';

/**
 * Chat (P1-D5). Layout from Austin's concept: session panel left, the
 * conversation in a 760px column, composer at the bottom with the standing
 * disclaimer. Attachments (D6, Backlog N5): a file for this conversation only,
 * not the library. Citations panel (D7) follows.
 */
export function ChatScreen() {
  const { me } = useSession();
  if (!me) return null;
  return <Chat userId={me.userId} />;
}

function Chat({ userId }: { userId: string }) {
  const runtime = useSimonRuntime(userId);
  useChatUrl(runtime); // /chat/<sessionId>: a refresh reopens the conversation
  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <div className="flex h-full">
        <SessionPanel />
        <Conversation />
      </div>
    </AssistantRuntimeProvider>
  );
}

function SessionPanel() {
  const [showArchived, setShowArchived] = useState(false);
  return (
    <ThreadListPrimitive.Root className="flex w-72 shrink-0 flex-col gap-1 border-r border-line bg-surface-alt p-3">
      <ThreadListPrimitive.New className="mb-3 flex items-center gap-2 rounded-lg bg-sage px-3 py-2 text-sm font-semibold text-white hover:bg-sage-dark">
        <Plus size={16} /> New chat
      </ThreadListPrimitive.New>
      <div className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-ink-faint">Conversations</div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <ThreadListPrimitive.Items>{() => <SessionCard />}</ThreadListPrimitive.Items>
        {/* Pages of 10 from sreg-api; hidden when there is no next page. */}
        <ThreadListPrimitive.LoadMore className="mt-1 w-full rounded-lg py-2 text-xs font-semibold text-sage hover:bg-surface disabled:hidden">
          Load more
        </ThreadListPrimitive.LoadMore>

        <button
          type="button"
          onClick={() => setShowArchived((v) => !v)}
          className="mt-4 flex w-full items-center gap-1 px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-ink-faint hover:text-ink"
          aria-expanded={showArchived}
        >
          {showArchived ? <ChevronDown size={12} /> : <ChevronRight size={12} />} Archived
        </button>
        {showArchived && <ThreadListPrimitive.Items archived>{() => <ArchivedCard />}</ThreadListPrimitive.Items>}
      </div>
    </ThreadListPrimitive.Root>
  );
}

const SUGGESTIONS = [
  'Which option notice deadlines fall in the next 12 months?',
  'Who has a right of first offer in the building?',
  'Summarize the rent roll as of the last close.',
  'What should I check before a renewal negotiation?',
];

function Conversation() {
  const { me } = useSession();
  return (
    <ThreadPrimitive.Root className="flex min-w-0 flex-1 flex-col bg-canvas">
      <ThreadPrimitive.Viewport className="flex-1 overflow-y-auto px-4">
        <div className="mx-auto flex max-w-conversation flex-col gap-6 py-8">
          <ThreadPrimitive.Empty>
            <div className="mt-16">
              <h1 className="font-display text-3xl font-semibold text-ink">
                Good {greeting()}, {me?.profile.firstName || me?.profile.displayName || 'there'}.
              </h1>
              <p className="mt-2 text-ink-soft">Ask about a lease, a tenant, an option or the rent roll.</p>
              <div className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {SUGGESTIONS.map((s) => (
                  <ThreadPrimitive.Suggestion
                    key={s}
                    prompt={s}
                    send
                    className="rounded-xl border border-line bg-surface px-4 py-3 text-left text-sm text-ink hover:border-sage-light"
                  >
                    {s}
                  </ThreadPrimitive.Suggestion>
                ))}
              </div>
            </div>
          </ThreadPrimitive.Empty>
          <ThreadPrimitive.Messages>
            {({ message }) => (message.role === 'user' ? <UserMessage /> : <AssistantMessage />)}
          </ThreadPrimitive.Messages>
          <Thinking />
          <RefreshSessionsOnRunEnd />
        </div>
      </ThreadPrimitive.Viewport>
      <Composer />
    </ThreadPrimitive.Root>
  );
}

function greeting(): string {
  const h = new Date().getHours();
  return h < 12 ? 'morning' : h < 18 ? 'afternoon' : 'evening';
}

function UserMessage() {
  return (
    <MessagePrimitive.Root className="flex justify-end">
      <div className="max-w-[85%] rounded-2xl rounded-br-md bg-ink px-4 py-2.5 text-[15px] text-white">
        <MessagePrimitive.Parts components={{ Text: UserText, File: () => null, Image: () => null }} />
      </div>
    </MessagePrimitive.Root>
  );
}

// Each attachment travels with a "[Attached: name]" label (attachments.ts): the
// bubble shows it as a chip. The file part itself is a gs:// link the browser can't open.
const ATTACHED = /\[Attached: ([^\]\n]+)\]/g;

export function attachedNames(text: string): { names: string[]; rest: string } {
  return { names: [...text.matchAll(ATTACHED)].map((m) => m[1]), rest: text.replace(ATTACHED, '').trim() };
}

function UserText({ text }: { text: string }) {
  const { names, rest } = attachedNames(text);
  return (
    <>
      {names.map((n, i) => (
        <span
          key={`${n}-${i}`}
          className="my-2 flex w-fit items-center gap-1.5 rounded-lg bg-white/10 px-2 py-1 text-xs"
        >
          <FileText size={13} /> {n}
        </span>
      ))}
      {rest && <span className="whitespace-pre-wrap">{rest}</span>}
    </>
  );
}

function ComposerAttachment() {
  return (
    <AttachmentPrimitive.Root className="flex items-center gap-1.5 rounded-lg border border-line bg-surface-alt px-2 py-1 text-xs text-ink">
      <FileText size={13} className="text-ink-soft" />
      <span className="max-w-[16rem] truncate">
        <AttachmentPrimitive.Name />
      </span>
      <AttachmentPrimitive.Remove className="text-ink-faint hover:text-ink" aria-label="Remove attachment">
        <X size={13} />
      </AttachmentPrimitive.Remove>
    </AttachmentPrimitive.Root>
  );
}

function MarkdownText() {
  return (
    <MarkdownTextPrimitive
      remarkPlugins={[remarkGfm]}
      className="prose-simon text-[15px] leading-relaxed text-ink [&_table]:my-3 [&_table]:w-full [&_table]:text-sm [&_td]:border-t [&_td]:border-line [&_td]:px-2 [&_td]:py-1 [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_th]:text-ink-soft [&_p]:my-2 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_strong]:font-semibold"
    />
  );
}

/**
 * Whether a reply has anything to show yet. Only text with characters counts:
 * tool calls (document search), tool results and reasoning arrive first while
 * Simon works, but render nothing, so they must not open an empty bubble.
 */
export function hasVisibleText(parts: readonly { type: string; text?: string }[]): boolean {
  return parts.some((p) => p.type === 'text' && (p.text ?? '').trim() !== '');
}

function AssistantMessage() {
  // Austin, 2026-10-23: no empty white strip. The row appears with the first
  // streamed text and grows from there. The ADK client also finishes a
  // tool-call step (document search) as its own message with no text: that
  // one never shows. A message that failed always shows, for its error.
  const failed = useAuiState((s) => s.message.status?.type === 'incomplete' && s.message.status.reason === 'error');
  const hasText = useAuiState((s) => hasVisibleText(s.message.parts));
  if (!hasText && !failed) return null;
  return (
    <MessagePrimitive.Root className="flex gap-3">
      <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-sage font-display text-xs font-bold text-white">
        S
      </div>
      <div className="min-w-0 flex-1 rounded-2xl rounded-tl-md bg-surface px-4 py-2 shadow-sm">
        <MessagePrimitive.Parts components={{ Text: MarkdownText }} />
        <MessagePrimitive.Error>
          <p className="mt-2 text-sm text-bronze">Simon couldn't finish that answer. Try again.</p>
        </MessagePrimitive.Error>
        <FeedbackBar />
      </div>
    </MessagePrimitive.Root>
  );
}

/**
 * When Simon finishes a reply, reload the session list: the BFF has recorded
 * the first-message preview (and last activity) in sreg-api by then.
 */
function RefreshSessionsOnRunEnd() {
  const running = useAuiState((s) => s.thread.isRunning);
  const was = useRef(running);
  const qc = useQueryClient();
  useEffect(() => {
    if (was.current && !running) void qc.invalidateQueries({ queryKey: sessionKeys.all });
    was.current = running;
  }, [running, qc]);
  return null;
}

function Thinking() {
  const running = useAuiState((s) => s.thread.isRunning);
  if (!running) return null;
  return (
    <div className="flex items-center gap-2 pl-10 text-sm text-ink-faint" aria-live="polite">
      <span>Simon is thinking</span>
      <span className="flex gap-1">
        <span className="simon-dot h-1.5 w-1.5 rounded-full bg-sage" />
        <span className="simon-dot h-1.5 w-1.5 rounded-full bg-sage" />
        <span className="simon-dot h-1.5 w-1.5 rounded-full bg-sage" />
      </span>
    </div>
  );
}

function Composer() {
  const running = useAuiState((s) => s.thread.isRunning);
  return (
    <div className="shrink-0 px-4 pb-4">
      <ComposerPrimitive.Root className="mx-auto flex max-w-conversation flex-col gap-2 rounded-2xl border border-line bg-surface p-2 shadow-sm focus-within:border-sage-light">
        <div className="flex flex-wrap gap-1.5 empty:hidden">
          <ComposerPrimitive.Attachments>{() => <ComposerAttachment />}</ComposerPrimitive.Attachments>
        </div>
        <div className="flex items-end gap-2">
          <ComposerPrimitive.AddAttachment
            multiple
            className="flex h-10 w-10 items-center justify-center rounded-xl text-ink-soft hover:bg-surface-alt hover:text-ink"
            aria-label="Attach a file"
            title={ATTACHMENT_HINT}
          >
            <Paperclip size={18} />
          </ComposerPrimitive.AddAttachment>
          <ComposerPrimitive.Input
            autoFocus
            rows={1}
            placeholder="Ask about a lease, a tenant, an option…"
            className="max-h-48 min-h-[40px] flex-1 resize-none bg-transparent px-2 py-2 text-[15px] text-ink outline-none placeholder:text-ink-faint"
          />
          {running ? (
            <ComposerPrimitive.Cancel
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-ink-soft text-white"
              aria-label="Stop"
            >
              <Square size={14} />
            </ComposerPrimitive.Cancel>
          ) : (
            <ComposerPrimitive.Send
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-ink text-white disabled:opacity-40"
              aria-label="Send"
            >
              <ArrowUp size={18} />
            </ComposerPrimitive.Send>
          )}
        </div>
      </ComposerPrimitive.Root>
      <p className="mx-auto mt-2 max-w-conversation text-center text-xs text-ink-faint">
        Answers cite the documents they came from. Check anything you act on.
      </p>
    </div>
  );
}
